// URL helpers shared by every API call, so no request ever has "//" after the host.
import config from "../config/config";

/** Joins a base URL and a path with exactly one "/" between them. The path keeps its trailing slash and query string. */
export const joinUrl = (base: string, path: string) =>
  String(base ?? "").replace(/\/+$/, "") + "/" + String(path ?? "").replace(/^\/+/, "");

/**
 * Returns a reason when the path part of a URL is malformed: "//" after the host
 * (usually an empty id, e.g. "/b2c/my_tickets//messages/") or a literal "undefined"/"null" segment.
 * The "//" in "http://" and anything in the query string are ignored.
 */
export const badPathReason = (url: string): string | null => {
  const path = String(url ?? "")
    .split("?")[0]
    .replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, "");
  if (/\/{2,}/.test(path)) return `Request path has an empty segment ("//"): ${path}`;
  if (/\/(undefined|null)(\/|$)/.test(path)) return `Request path has a missing value: ${path}`;
  return null;
};

/**
 * Guards a value used as a path segment (an id in "/x/{id}/").
 * Returns the id as a string, or throws when it is empty / undefined.
 */
export const pathSegment = (value: unknown, name = "id"): string => {
  const s = value === null || value === undefined ? "" : String(value).trim();
  if (!s || s === "undefined" || s === "null") throw new Error(`Missing ${name} for this request.`);
  return s;
};

/** Runs `build` (which may call pathSegment) and turns a thrown guard error into a rejected promise. */
export const guarded = <T>(build: () => Promise<T>): Promise<T> => {
  try {
    return build();
  } catch (err) {
    return Promise.reject(err);
  }
};

/**
 * Cleans a link the backend hands us (attachment, file download) before it goes into
 * an href/src/window.open: collapses "//" in the path when the link is on our own API host.
 * Links to other hosts (Google Drive, S3 signed URLs) are returned untouched.
 */
export const cleanLink = (url: string | null | undefined): string => {
  const raw = String(url ?? "").trim();
  const m = raw.match(/^([a-z][a-z0-9+.-]*:\/\/[^/?#]*)([^?#]*)(.*)$/i);
  if (!m) return raw;
  const [, origin, path, rest] = m;
  const apiOrigin = String(config.APIDEV).match(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i)?.[0];
  if (!apiOrigin || origin.toLowerCase() !== apiOrigin.toLowerCase()) return raw;
  return origin + path.replace(/\/{2,}/g, "/") + rest;
};
